import { useEffect, useState } from 'react'
import type { GymLeaderboardEntry, GymLeaderboardMetric } from '@gymboard/types'
import { screenApi } from '../lib/api'
import { isFeatureDisabled } from './useScreenFeatures'

export const BOARD_METRICS: GymLeaderboardMetric[] = ['classes', 'minutes', 'calories']
/** With gym-floor check-in on, days at the gym lead: anyone can win them. */
export const FLOOR_BOARD_METRICS: GymLeaderboardMetric[] = ['visits', ...BOARD_METRICS]

export interface GymBoard {
  entries: GymLeaderboardEntry[]
  totalParticipants: number
}

export type GymBoards = Partial<Record<GymLeaderboardMetric, GymBoard>>

const REFRESH_MS = 120_000

/**
 * This week's gym-wide boards for the idle screen. A failed fetch keeps the
 * last good board for that metric, so a network blip never blanks the TV —
 * except when the gym has switched the leaderboard off (`enabled` false, or
 * the API says FEATURE_DISABLED): then the boards are cleared.
 */
export function useGymBoards(rows = 8, metrics: GymLeaderboardMetric[] = BOARD_METRICS, enabled = true): GymBoards {
  const [boards, setBoards] = useState<GymBoards>({})
  const metricsKey = metrics.join(',')

  useEffect(() => {
    if (!enabled) {
      setBoards({})
      return
    }
    let cancelled = false
    const load = async () => {
      const results = await Promise.allSettled(
        metrics.map((m) => screenApi.getGymLeaderboard(m, 'week', rows)),
      )
      if (cancelled) return
      if (results.some((r) => r.status === 'rejected' && isFeatureDisabled(r.reason))) {
        setBoards({})
        return
      }
      setBoards((prev) => {
        const next: GymBoards = { ...prev }
        results.forEach((r, i) => {
          if (r.status !== 'fulfilled') return
          const { entries, totalParticipants } = r.value.data
          next[metrics[i]] = { entries: entries.filter((e) => e.value > 0), totalParticipants }
        })
        return next
      })
    }
    load()
    const interval = setInterval(load, REFRESH_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, metricsKey, enabled])

  return boards
}
